import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CategoryIcon from '@mui/icons-material/Category';
import PublicIcon from '@mui/icons-material/Public';
import CodeIcon from '@mui/icons-material/Code';
import InsightsIcon from '@mui/icons-material/Insights';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TodayIcon from '@mui/icons-material/Today';
import ViewListIcon from '@mui/icons-material/ViewList';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import {
  buildWorkspaceChips,
  buildWorkspaceSidebarItems,
  normalizeWorkspaceDescriptor,
  normalizeWorkspaceRoute,
} from '../../components/common/AnalyticsWorkspace';
import { DEV_VIEWS } from '../../constants/developerMetrics';
import { getDevFlag } from '../../constants/devFlags';
import { shouldUseLocalOrFixtureData } from '../../utils/dataLoader';
import { artifactCatalogView, artifactDetailView } from './subpages/artifacts/artifactDescriptors';
import {
  FREE_SECURITY_ANALYSIS_SAMPLE_CHIP_LABEL,
  isPublicResearchSampleTicker,
} from '../../constants/api';
import { isPrivateCompanyIdentifier } from '../../utils/companyHelpers';
import { ACCESS_POLICY_IDS } from '../../configs/accessPolicies';
import {
  SERVICE_HEALTH_LABEL,
  SERVICE_HEALTH_VIEW_ID,
  companyServiceHealthView,
  marketServiceHealthView,
} from './subpages/serviceHealth/serviceHealthDescriptors';

export const DEVELOPER_WORKSPACE_LAYERS = {
  CATEGORY: 'category',
  PROJECT: 'project',
  PROJECT_DETAIL: 'project-detail',
  ARTIFACT: 'artifact',
};

export const DEVELOPER_WORKSPACE_VIEWS = {
  TRENDS: 'trends',
  LEADERBOARD: 'leaderboard',
  PACKAGES: 'packages',
  DAILY: 'daily',
  VERSIONS: 'versions',
  SERVICE_HEALTH: SERVICE_HEALTH_VIEW_ID,
  CATALOG: 'catalog',
  DETAIL: 'detail',
};

export const SHOW_DAILY_DEVELOPER = getDevFlag('DEVELOPER_SIGNALS_DAILY');
export const SHOW_DEVELOPER_LEADERBOARD = getDevFlag('DEVELOPER_SIGNALS_LEADERBOARD');
export const SHOW_ADOPTION_SIGNALS_V2 = getDevFlag('ADOPTION_SIGNALS_V2');
// Dev-only: absent from production builds, so routes normalize to the layer default.
export const SHOW_SERVICE_HEALTH = getDevFlag('SERVICE_HEALTH');
export const SHOW_ARTIFACT_CATALOG = getDevFlag('ADOPTION_ARTIFACT_CATALOG') && shouldUseLocalOrFixtureData();

export const DEVELOPER_WORKSPACE_SECTION = DEVELOPER_WORKSPACE_LAYERS.PROJECT;
export const DEVELOPER_WORKSPACE_PATH_BASE = '/adoption';

export const DEVELOPER_WORKSPACE_VIEW_LABELS = {
  [DEV_VIEWS.OVERVIEW]: 'Overview',
  [DEV_VIEWS.DETAIL]: 'Project Analysis',
  [DEV_VIEWS.LEADERBOARD]: 'Leaderboard',
  [DEVELOPER_WORKSPACE_VIEWS.TRENDS]: 'Dashboard',
  [DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD]: 'Leaderboard',
  [DEVELOPER_WORKSPACE_VIEWS.PACKAGES]: 'Packages',
  [DEVELOPER_WORKSPACE_VIEWS.DAILY]: 'Daily',
  [DEVELOPER_WORKSPACE_VIEWS.VERSIONS]: 'Versions',
  [DEVELOPER_WORKSPACE_VIEWS.SERVICE_HEALTH]: SERVICE_HEALTH_LABEL,
};

export const DEFAULT_DEVELOPER_WORKSPACE_NAV = {
  layer: DEVELOPER_WORKSPACE_LAYERS.PROJECT,
  view: DEVELOPER_WORKSPACE_VIEWS.TRENDS,
};

export const isDeveloperResearchViewGateActive = ({
  hasResearchAccess = false,
  isPublicDailySample = false,
  view,
} = {}) => (
  view === DEVELOPER_WORKSPACE_VIEWS.VERSIONS
    ? !hasResearchAccess
    : view === DEVELOPER_WORKSPACE_VIEWS.DAILY
      ? !hasResearchAccess && !isPublicDailySample
      : false
);

export const DEVELOPER_WORKSPACE_VIEWS_BY_LAYER = {
  [DEVELOPER_WORKSPACE_LAYERS.ARTIFACT]: SHOW_ARTIFACT_CATALOG ? ['catalog', 'detail'] : [],
  [DEVELOPER_WORKSPACE_LAYERS.CATEGORY]: [
    DEVELOPER_WORKSPACE_VIEWS.TRENDS,
    ...(SHOW_SERVICE_HEALTH ? [DEVELOPER_WORKSPACE_VIEWS.SERVICE_HEALTH] : []),
  ],
  [DEVELOPER_WORKSPACE_LAYERS.PROJECT]: [
    DEVELOPER_WORKSPACE_VIEWS.TRENDS,
    ...(SHOW_DAILY_DEVELOPER ? [DEVELOPER_WORKSPACE_VIEWS.DAILY] : []),
    ...(SHOW_DEVELOPER_LEADERBOARD ? [DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD] : []),
  ],
  [DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL]: [
    DEVELOPER_WORKSPACE_VIEWS.PACKAGES,
    ...(SHOW_DAILY_DEVELOPER ? [DEVELOPER_WORKSPACE_VIEWS.DAILY] : []),
    DEVELOPER_WORKSPACE_VIEWS.VERSIONS,
    ...(SHOW_SERVICE_HEALTH ? [DEVELOPER_WORKSPACE_VIEWS.SERVICE_HEALTH] : []),
  ],
};

const LEGACY_VIEW_NAV = {
  [DEV_VIEWS.OVERVIEW]: {
    layer: DEVELOPER_WORKSPACE_LAYERS.PROJECT,
    view: DEVELOPER_WORKSPACE_VIEWS.TRENDS,
  },
  [DEV_VIEWS.DETAIL]: {
    layer: DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL,
    view: DEVELOPER_WORKSPACE_VIEWS.PACKAGES,
  },
  [DEV_VIEWS.LEADERBOARD]: {
    ...(SHOW_DEVELOPER_LEADERBOARD ? {
      layer: DEVELOPER_WORKSPACE_LAYERS.PROJECT,
      view: DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD,
    } : DEFAULT_DEVELOPER_WORKSPACE_NAV),
  },
};

export const normalizeDeveloperWorkspaceNav = ({
  layer,
  view,
  legacyView,
} = {}) => {
  if (legacyView && LEGACY_VIEW_NAV[legacyView]) {
    return LEGACY_VIEW_NAV[legacyView];
  }

  if (
    SHOW_DEVELOPER_LEADERBOARD
    && layer === DEVELOPER_WORKSPACE_LAYERS.CATEGORY
    && view === DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD
  ) {
    return {
      layer: DEVELOPER_WORKSPACE_LAYERS.PROJECT,
      view: DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD,
    };
  }

  const normalizedLayer = Object.values(DEVELOPER_WORKSPACE_LAYERS).includes(layer) && (layer !== 'artifact' || SHOW_ARTIFACT_CATALOG)
    ? layer
    : DEFAULT_DEVELOPER_WORKSPACE_NAV.layer;
  const viewOptions = DEVELOPER_WORKSPACE_VIEWS_BY_LAYER[normalizedLayer] || [];
  const normalizedView = viewOptions.includes(view)
    ? view
    : viewOptions[0] || DEFAULT_DEVELOPER_WORKSPACE_NAV.view;

  return {
    layer: normalizedLayer,
    view: normalizedView,
  };
};

export const normalizeDeveloperWorkspaceView = (view) => (
  LEGACY_VIEW_NAV[view] ? view : DEV_VIEWS.OVERVIEW
);

export const getDeveloperRenderView = (layer, view) => {
  if (view === DEVELOPER_WORKSPACE_VIEWS.VERSIONS) return DEVELOPER_WORKSPACE_VIEWS.VERSIONS;
  if (view === DEVELOPER_WORKSPACE_VIEWS.DAILY) return DEV_VIEWS.DAILY;
  if (view === DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD) return DEV_VIEWS.LEADERBOARD;
  if (layer === DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL) return DEV_VIEWS.DETAIL;
  return DEV_VIEWS.OVERVIEW;
};

export const buildDeveloperWorkspacePath = ({
  layer,
  sectionId,
  view,
  viewId,
  ticker,
  projectId,
  artifactId,
  panel,
} = {}) => {
  const nav = normalizeDeveloperWorkspaceNav({
    layer: layer || sectionId,
    view: view || viewId,
  });
  const params = new URLSearchParams();
  if (ticker && nav.layer === DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL) {
    params.set('ticker', String(ticker).trim().toUpperCase());
  }
  if (projectId && nav.layer === DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL) {
    params.set('projectId', String(projectId).trim());
  }
  if (nav.layer === 'artifact' && artifactId) params.set('artifactId', artifactId);
  if (nav.layer === 'artifact' && nav.view !== 'catalog') params.set('panel', nav.view === 'detail' ? (panel || 'overview') : nav.view);
  const query = params.toString();
  return `${DEVELOPER_WORKSPACE_PATH_BASE}/${nav.layer}/${nav.layer === 'artifact' && nav.view !== 'catalog' ? 'detail' : nav.view}${query ? `?${query}` : ''}`;
};

export const buildLegacyDeveloperRedirectPath = ({
  view,
  ticker,
  projectId,
} = {}) => {
  const nav = normalizeDeveloperWorkspaceNav({ legacyView: view });
  return buildDeveloperWorkspacePath({
    layer: nav.layer,
    view: nav.view,
    ticker,
    projectId,
  });
};

const OVERVIEW_CHART_SKELETON = {
  kind: 'chart',
  preset: 'trendLine',
  variant: 'line',
  height: '35vh',
  width: '80%',
  minHeight: 260,
};

const OVERVIEW_TABLE_SKELETON = {
  kind: 'table',
  preset: 'compsheet',
  rows: 15,
  columns: 8,
  tableWidth: '80%',
  height: 300,
};

const DETAIL_CHART_SKELETON = {
  kind: 'chart',
  preset: 'trendLine',
  variant: 'line',
  height: '45vh',
  width: '80%',
  minHeight: 300,
};

const DETAIL_TABLE_SKELETON = {
  kind: 'table',
  preset: 'compsheet',
  rows: 10,
  columns: 6,
  tableWidth: '80%',
  height: 250,
};

const LEADERBOARD_TABLE_SKELETON = {
  kind: 'table',
  preset: 'signal',
  rows: 15,
  columns: 5,
  tableWidth: 700,
  height: '70vh',
};

const overviewView = ({
  id,
  label,
  description,
  icon = ViewListIcon,
  controls = ['ticker', 'category', 'ownership', 'aiProfile', 'weeks'],
  layout = 'chartTable',
  panels,
  visualizations = ['trendLine', 'groupedBar', 'table'],
  tableColumns = ['company', 'ticker', 'category', 'metricValue', 'trendDelta'],
} = {}) => ({
  id,
  label,
  description,
  icon,
  requiredData: 'developerMetrics',
  controls,
  layout,
  panels: panels || [
    { id: 'projectTrend', kind: 'chart', skeleton: OVERVIEW_CHART_SKELETON },
    { id: 'projects', kind: 'table', skeleton: OVERVIEW_TABLE_SKELETON },
  ],
  visualizations,
  tableColumns,
});

const leaderboardView = ({ description } = {}) => ({
  id: DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD,
  label: DEVELOPER_WORKSPACE_VIEW_LABELS[DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD],
  description,
  icon: LeaderboardIcon,
  requiredData: 'developerMetrics',
  controls: ['category', 'ownership', 'aiProfile', 'metric', 'rows', 'sort', 'view'],
  layout: 'table',
  panels: [
    { id: 'leaderboard', kind: 'table', skeleton: LEADERBOARD_TABLE_SKELETON },
  ],
  visualizations: ['table'],
  tableColumns: ['rank', 'company', 'ticker', 'category', 'metricValue', 'trendDelta', 'percent'],
});

const projectDetailView = ({
  id,
  label,
  description,
  icon = InsightsIcon,
  accessPolicyId,
  controls = ['companySearch', 'weeks'],
} = {}) => ({
  id,
  label,
  description,
  icon,
  ...(accessPolicyId ? { accessPolicyId } : {}),
  requiredData: 'developerMetrics',
  controls,
  layout: 'chartTable',
  panels: [
    { id: 'packageTrend', kind: 'chart', skeleton: DETAIL_CHART_SKELETON },
    { id: 'packages', kind: 'table', skeleton: DETAIL_TABLE_SKELETON },
  ],
  visualizations: ['trendLine', 'groupedBar', 'table'],
  tableColumns: ['company', 'category', 'metricValue', 'trendDelta'],
});

const dailyView = ({ description, detail = false } = {}) => ({
  id: DEVELOPER_WORKSPACE_VIEWS.DAILY,
  label: DEVELOPER_WORKSPACE_VIEW_LABELS[DEVELOPER_WORKSPACE_VIEWS.DAILY],
  description,
  icon: TodayIcon,
  accessPolicyId: ACCESS_POLICY_IDS.DAILY_DEVELOPER_DASHBOARD,
  requiredData: 'dailyDeveloperMetrics',
  controls: detail
    ? ['companySearch', 'project', 'analysis', 'days']
    : ['ticker', 'category', 'ownership', 'aiProfile', 'baseline', 'dateRange', 'minimum'],
  layout: 'chartTable',
  panels: [
    { id: detail ? 'packageDailyTrend' : 'projectDailyTrend', kind: 'chart', skeleton: OVERVIEW_CHART_SKELETON },
    { id: detail ? 'packageDailySignals' : 'projectDailySignals', kind: 'table', skeleton: OVERVIEW_TABLE_SKELETON },
  ],
  visualizations: ['trendLine', 'groupedBar', 'table'],
  tableColumns: [
    ...(detail ? [
      'package',
      'current',
      'delta1d',
      'delta1dPct',
      'delta7d',
      'delta7dPct',
      'delta14d',
      'delta14dPct',
      'delta28d',
      'delta28dPct',
      'compositeMovePct',
    ] : [
      'project',
      'packages',
      'npmRolling7d',
      'pypiRolling7d',
      'nugetRolling7d',
      'dockerRolling7d',
      'terraformRolling7d',
      'compositeMovePct',
    ]),
  ],
});

export const DEVELOPER_WORKSPACE_DESCRIPTOR = {
  id: 'industryDeveloperMetrics',
  title: 'Adoption Signals',
  description: 'Project, package, and infrastructure adoption signals across developer platforms.',
  icon: CodeIcon,
  defaultSectionId: DEFAULT_DEVELOPER_WORKSPACE_NAV.layer,
  defaultViewId: DEFAULT_DEVELOPER_WORKSPACE_NAV.view,
  sections: [
    ...(SHOW_ARTIFACT_CATALOG ? [{ id: 'artifact', label: 'Artifacts', description: 'Package metadata and lifecycle history', icon: Inventory2OutlinedIcon, views: [artifactCatalogView(), artifactDetailView()] }] : []),
    {
      id: DEVELOPER_WORKSPACE_LAYERS.CATEGORY,
      label: 'Market',
      description: 'Market-level adoption signal trends',
      icon: PublicIcon,
      views: [
        overviewView({
          id: DEVELOPER_WORKSPACE_VIEWS.TRENDS,
          label: DEVELOPER_WORKSPACE_VIEW_LABELS[DEVELOPER_WORKSPACE_VIEWS.TRENDS],
          description: 'Market usage benchmark, growth, share, and contribution',
          icon: CategoryIcon,
          controls: ['category', 'ownership', 'aiProfile', 'weeks'],
          panels: [
            { id: 'categoryBenchmark', kind: 'chart', skeleton: OVERVIEW_CHART_SKELETON },
            { id: 'categoryProjectMix', kind: 'table', skeleton: OVERVIEW_TABLE_SKELETON },
          ],
          tableColumns: ['company', 'category', 'metricValue', 'trendDelta', 'percent'],
        }),
        ...(SHOW_SERVICE_HEALTH ? [marketServiceHealthView()] : []),
      ],
    },
    {
      id: DEVELOPER_WORKSPACE_LAYERS.PROJECT,
      label: 'Project',
      description: 'Project and company-specific adoption trends',
      icon: InsightsIcon,
      views: [
        overviewView({
          id: DEVELOPER_WORKSPACE_VIEWS.TRENDS,
          label: DEVELOPER_WORKSPACE_VIEW_LABELS[DEVELOPER_WORKSPACE_VIEWS.TRENDS],
          description: 'Project screener with selected-project usage trend',
          controls: ['ticker', 'category', 'ownership', 'aiProfile', 'weeks'],
          panels: [
            { id: 'projectTrend', kind: 'chart', skeleton: OVERVIEW_CHART_SKELETON },
            { id: 'projectScreener', kind: 'table', skeleton: OVERVIEW_TABLE_SKELETON },
          ],
        }),
        ...(SHOW_DAILY_DEVELOPER ? [dailyView({
          description: 'Rolling seven-day project adoption and growth versus ecosystem averages',
        })] : []),
        ...(SHOW_DEVELOPER_LEADERBOARD ? [leaderboardView({
          description: 'Project movers and category share leaders',
        })] : []),
      ],
    },
    {
      id: DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL,
      label: 'Project Detail',
      description: 'Company, project, artifact, and version analysis',
      icon: AccountTreeIcon,
      views: [
        projectDetailView({
          id: DEVELOPER_WORKSPACE_VIEWS.PACKAGES,
          label: DEVELOPER_WORKSPACE_VIEW_LABELS[DEVELOPER_WORKSPACE_VIEWS.PACKAGES],
          description: 'Package-level company and project analysis',
          controls: ['companySearch', 'project', 'analysis', 'weeks'],
        }),
        ...(SHOW_DAILY_DEVELOPER ? [dailyView({
          description: 'Daily package-level changes for a selected company and project',
          detail: true,
        })] : []),
        projectDetailView({
          id: DEVELOPER_WORKSPACE_VIEWS.VERSIONS,
          label: DEVELOPER_WORKSPACE_VIEW_LABELS[DEVELOPER_WORKSPACE_VIEWS.VERSIONS],
          description: 'Release activity and version share for a selected project artifact',
          icon: Inventory2OutlinedIcon,
          accessPolicyId: ACCESS_POLICY_IDS.DEVELOPER_VERSIONS_DASHBOARD,
          controls: ['companySearch', 'project', 'versionArtifact', 'versionGrain', 'versionRange', 'versionMode'],
        }),
        ...(SHOW_SERVICE_HEALTH ? [companyServiceHealthView()] : []),
      ],
    },
  ],
};

export const DEVELOPER_WORKSPACE_CONFIG = {
  ...normalizeWorkspaceDescriptor(DEVELOPER_WORKSPACE_DESCRIPTOR),
  buildPath: ({ sectionId, viewId, ticker, projectId, artifactId, panel }) => (
    buildDeveloperWorkspacePath({
      layer: sectionId,
      view: viewId,
      ticker,
      projectId,
      artifactId,
      panel: sectionId === 'artifact' && viewId === 'detail' ? 'overview' : panel,
    })
  ),
  resolveRoute: ({ sectionId, viewId }) => {
    const nav = normalizeDeveloperWorkspaceNav({
      layer: sectionId,
      view: viewId,
    });

    return normalizeWorkspaceRoute({
      sections: DEVELOPER_WORKSPACE_CONFIG.sections,
      defaultSectionId: DEVELOPER_WORKSPACE_CONFIG.defaultSectionId,
      defaultViewId: DEVELOPER_WORKSPACE_CONFIG.defaultViewId,
      sectionId: nav.layer,
      viewId: nav.view,
    });
  },
  shouldRedirect: ({ sectionId, viewId, resolvedRoute }) => (
    sectionId !== resolvedRoute.sectionId
    || viewId !== resolvedRoute.viewId
  ),
  getExtraChips: ({ routeState = {} }) => {
    const chips = [];
    if (routeState.companyName) chips.push({ label: routeState.companyName });
    if (routeState.ticker && !isPrivateCompanyIdentifier(routeState.ticker)) {
      chips.push({ label: String(routeState.ticker).toUpperCase() });
    }
    if (routeState.projectName) chips.push({ label: routeState.projectName });
    if (isPublicResearchSampleTicker(routeState.ticker)) {
      chips.push({ label: FREE_SECURITY_ANALYSIS_SAMPLE_CHIP_LABEL });
    }
    return chips;
  },
  getChips: ({ routeState = {}, resolvedRoute }) => (
    buildWorkspaceChips({
      section: resolvedRoute.section,
      view: resolvedRoute.view,
      routeState,
      extraChips: DEVELOPER_WORKSPACE_CONFIG.getExtraChips({ routeState, resolvedRoute }),
    })
  ),
};

export const getDeveloperWorkspaceRoute = ({ layer, view } = {}) => {
  const nav = normalizeDeveloperWorkspaceNav({ layer, view });
  return normalizeWorkspaceRoute({
    sections: DEVELOPER_WORKSPACE_CONFIG.sections,
    defaultSectionId: DEVELOPER_WORKSPACE_CONFIG.defaultSectionId,
    defaultViewId: DEVELOPER_WORKSPACE_CONFIG.defaultViewId,
    sectionId: nav.layer,
    viewId: nav.view,
  });
};

export const buildDeveloperWorkspaceSidebarItems = ({ ticker, projectId } = {}) => (
  buildWorkspaceSidebarItems({
    sections: DEVELOPER_WORKSPACE_CONFIG.sections,
    buildPath: ({ sectionId, viewId }) => buildDeveloperWorkspacePath({
      layer: sectionId,
      view: viewId,
      ticker,
      projectId,
    }),
  })
);

export const buildDeveloperWorkspaceChips = ({
  layer,
  view,
  ticker,
  projectName,
  companyName,
} = {}) => {
  const route = getDeveloperWorkspaceRoute({ layer, view });
  return buildWorkspaceChips({
    section: route.section,
    view: route.view,
    extraChips: DEVELOPER_WORKSPACE_CONFIG.getExtraChips({
      routeState: { ticker, projectName, companyName },
      resolvedRoute: route,
    }),
  });
};
